import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { PaymentMethod, PaymentTerm } from '@superdispatch/sdk';
import { LocalStore } from 'shared/helpers/Store';
import { z } from 'zod';
import { ATTACHMENT_FILE_TYPES, LOAD_STATUSES } from '../../loads/data/LoadDTO';

const ORDER_BY_MODIFIED_DATE_STORAGE_KEY = 'trips_order_by_modified_date';

const TRUE_PATTERN = /^(true|1)$/i;
const FALSE_PATTERN = /^(false|0)$/i;

function coerceBoolean(value: unknown) {
  return typeof value === 'string' || typeof value === 'number'
    ? TRUE_PATTERN.test(String(value))
    : value;
}

function toBooleanParam(value: unknown): unknown {
  if (typeof value === 'boolean') {
    return value;
  }

  if (TRUE_PATTERN.test(String(value))) {
    return true;
  }

  return FALSE_PATTERN.test(String(value)) ? false : null;
}

function toNumberParam(value: unknown, fallback: number): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  const text = String(value ?? '').trim();
  const parsed = text === '' ? NaN : Number(text);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function toStringParam(value: unknown): unknown {
  const next = String(value ?? '').trim();

  return next.length > 0 ? next : null;
}

//
// VenueDTO
//

const baseVenueShape = {
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  type: z.enum(['origin', 'destination']).catch('origin'),
};

export type VenueDTO = z.infer<typeof venueSchema>;
export const venueSchema = z.object({
  ...baseVenueShape,
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

const routeShape = {
  origin: venueSchema.nullable(),
  destination: venueSchema.nullable(),
};

const responseRouteSchema = z.object({
  origin: venueSchema.nullish().default(null),
  destination: venueSchema.nullish().default(null),
});

//
// TripStatus
//

export type TripStatus = (typeof TRIP_STATUSES)[number];
export const TRIP_STATUSES = [
  'planning',
  'ready',
  'in_progress',
  'delivered',
  'completed',
  'archived',
  'none',
] as const;

const tripStatusShape = {
  status: z.enum(TRIP_STATUSES).nullable().default(null).catch(null).optional(),
  is_driver_paid: z
    .preprocess(coerceBoolean, z.boolean().nullable().catch(false))
    .default(false)
    .optional(),
  is_paid_by_customer: z
    .preprocess(coerceBoolean, z.boolean().nullable().catch(false))
    .default(false)
    .optional(),
  is_customer_billed: z
    .preprocess(coerceBoolean, z.boolean().nullable().catch(false))
    .default(false)
    .optional(),
  is_completed: z
    .preprocess(coerceBoolean, z.boolean().nullable().catch(false))
    .default(false)
    .optional(),
};

//
// EditTripDTO
//

export type EditTripDTO = z.infer<typeof editTripSchema>;
const editTripSchema = z.object({
  ...tripStatusShape,
  name: z
    .string()
    .max(100, 'Maximum length is 100 characters')
    .nullish()
    .transform((value) => value ?? '')
    .refine((value) => value.length > 0, 'Please, input trip name')
    .prefault(''),
  driver_guid: z
    .string()
    .nullish()
    .transform((value) => value ?? '')
    .optional(),
  load_guids: z
    .array(
      z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
    )
    .prefault([])
    .optional(),
  route: z.object(routeShape).optional(),
});

//
// TripDTO
//

const tripLoadLegacySchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  number: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  status: z.enum(LOAD_STATUSES).catch('new'),
  customer_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  broker_fee: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),

  load_order: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),

  pickup: z.object({
    address: z
      .string()
      .nullish()
      .transform((value) => value ?? ''),
    date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null),
    ),
    scheduled_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null),
    ),
    latitude: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
    longitude: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
  }),

  delivery: z.object({
    address: z
      .string()
      .nullish()
      .transform((value) => value ?? ''),
    date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null),
    ),
    scheduled_date: z.preprocess(
      (value) =>
        stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
      z.string().nullable().default(null),
    ),
    latitude: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
    longitude: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
  }),

  payment: z.object({
    amount: z
      .string()
      .nullish()
      .transform((value) => value ?? ''), // BigDecimal
    term: z
      .string()
      .nullish()
      .transform((value) => value ?? ''),
    status: z.enum(['billed', 'paid']).nullable().default(null).catch(null),
  }),

  driver: z
    .object({
      guid: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      name: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
    })
    .nullable(),

  vehicles: z
    .array(
      z.object({
        year: z.preprocess(
          (value) => (value == null || value === '' ? null : Number(value)),
          z.number().nullable().default(null).catch(null),
        ),
        maker: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
        model: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
      }),
    )
    .prefault([]),
  terminals: z
    .array(
      z.object({
        guid: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
        name: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
        is_current: z.preprocess(
          coerceBoolean,
          z.boolean().nullable().default(false).catch(false),
        ),
        is_delivered: z.preprocess(
          coerceBoolean,
          z.boolean().nullable().default(false).catch(false),
        ),
      }),
    )
    .prefault([]),
});

export type TripDriverDTO = z.infer<typeof tripDriverSchema>;
const tripDriverSchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  email: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  phone: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export type TripAttachmentDTO = z.infer<typeof tripAttachmentSchema>;
const tripAttachmentSchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  type: z.enum(ATTACHMENT_FILE_TYPES).catch('any'),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  url: z
    .string()
    .nullish()
    .transform((value) => value ?? '')
    .refine(
      (value) => value.length > 0,
      'Wait for upload(s) to finish successfully',
    )
    .prefault(''),
});

export const internalNoteTextSchema = z.object({
  text: z
    .string()
    .max(500, 'Maximum length is 500 characters')
    .nullish()
    .transform((value) => value ?? ''),
});

export type TripNoteDTO = z.infer<typeof tripNoteSchema>;
const tripNoteSchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  created_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  author_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  text: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

const tripInternalNoteItemSchema = z.object({
  text: z
    .string()
    .max(500, 'Maximum length is 500 characters')
    .nullish()
    .transform((value) => value ?? ''),
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  created_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  author: z.object({
    name: z
      .string()
      .nullish()
      .transform((value) => value ?? ''),
  }),
});

export type TripDTO = z.infer<typeof tripSchema>;
export const tripSchema = z.object({
  ...tripStatusShape,
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  is_archived: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  modified_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),

  total_price: z
    .string()
    .nullish()
    .transform((value) => value ?? ''), // BigDecimal
  expenses_sum: z
    .string()
    .nullish()
    .transform((value) => value ?? ''), // BigDecimal
  broker_fee_sum: z
    .string()
    .nullish()
    .transform((value) => value ?? ''), // BigDecimal
  driver_pay_sum: z
    .string()
    .nullish()
    .transform((value) => value ?? ''), // BigDecimal

  map_route_url: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),

  driver: tripDriverSchema.nullable(),

  loads: z.array(tripLoadLegacySchema).prefault([]),

  vehicles_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  vehicles_weight: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  attachments: z.array(tripAttachmentSchema).prefault([]).optional(),
  internal_notes: z.array(tripInternalNoteItemSchema).prefault([]).optional(),
  route: responseRouteSchema.optional(),
});

export type TripActionsDTO = Omit<
  TripDTO,
  | 'total_price'
  | 'expenses_sum'
  | 'broker_fee_sum'
  | 'driver_pay_sum'
  | 'internal_notes'
  | 'attachments'
  | 'modified_date'
  | 'vehicles_count'
  | 'vehicles_weight'
>;

//
// TripShortDTO
//

export type TripShortDTO = z.infer<typeof tripShortSchema>;
export const tripShortSchema = z.object({
  ...tripStatusShape,
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  archived: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  changed_at: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  price: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  distance_in_miles: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  price_per_mile: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),

  driver: z
    .object({
      guid: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      name: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      email: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
    })
    .nullable(),

  dispatcher: z
    .object({
      guid: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      name: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
    })
    .nullable(),

  loads: z.object({
    total: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
    new: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
    delivered: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
    picked_up: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
    in_terminal: z.preprocess(
      (value) => (value == null || value === '' ? null : Number(value)),
      z.number().nullable().default(null).catch(null),
    ),
  }),

  route: responseRouteSchema.optional(),
});

//
// LoadForTripDTO
//

const tripVehicleSchema = z.object({
  is_inoperable: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  make: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  model: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  price: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  vin: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  year: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  weight: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

const loadTerminalSchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export type LoadForTripDTO = z.infer<typeof loadForTripSchema>;
export const loadForTripSchema = z.object({
  id: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  load_order: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  load_id: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  number: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  status: z.enum(LOAD_STATUSES).catch('new'),
  pickup_phone: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  pickup_contact: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  delivery_phone: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  delivery_contact: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  delivery_latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  delivery_longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  pickup_latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  pickup_longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  origin: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  origin_date: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  destination: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  destination_date: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  customer: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  payment: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  broker_fee: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  driver_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  driver_id: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  is_active: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  has_inoperable_vehicle: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  vehicles: z.array(tripVehicleSchema).prefault([]),
  terminals: z.array(loadTerminalSchema).prefault([]),
  current_leg: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

//
// TripsPageParams
//

const TRIPS_PAGE_STATUS_PARAMS = ['archived', 'unarchived'] as const;

export type TripsPageOrderingParam =
  | (typeof TRIPS_PAGE_ORDERING_PARAMS)[number]
  | null;

export const TRIPS_PAGE_ORDERING_PARAMS = [
  'changed_at',
  '-changed_at',
  'loads',
  '-loads',
  'distance',
  '-distance',
] as const;

export type TripsPageParams = z.infer<typeof tripsPageParamsSchema>;

export const tripsPageParamsSchema = z.object({
  page: z.preprocess(
    (value) => toNumberParam(value, 1),
    z.number().default(1).catch(1),
  ),
  driver_guid: z.preprocess(
    toStringParam,
    z.string().nullable().default(null).catch(null),
  ),
  dispatcher_guid: z.preprocess(
    toStringParam,
    z.string().nullable().default(null).catch(null),
  ),
  page_size: z.preprocess(
    (value) => toNumberParam(value, 25),
    z.number().default(25).catch(25),
  ),
  query: z.preprocess(
    (value) => toStringParam(value) ?? '',
    z.string().default('').catch(''),
  ),
  search_type: z.enum(['trip', 'driver']).catch('trip'),
  status: z
    .enum(TRIPS_PAGE_STATUS_PARAMS)
    .nullable()
    .default(null)
    .catch(null)
    .optional(),
  trip_status: z
    .preprocess(toStringParam, z.string().nullable().default(null).catch(null))
    .default(null)
    .optional(),
  is_customer_billed: z
    .preprocess(toBooleanParam, z.boolean().nullable().catch(null))
    .default(null)
    .optional(),
  is_driver_paid: z
    .preprocess(toBooleanParam, z.boolean().nullable().catch(null))
    .default(null)
    .optional(),
  is_paid_by_customer: z
    .preprocess(toBooleanParam, z.boolean().nullable().catch(null))
    .default(null)
    .optional(),
  ordering: z
    .enum(TRIPS_PAGE_ORDERING_PARAMS)
    .nullable()
    .default(null)
    .catch(null),
});

export function getDefaultTripLoadsOrdering(): TripsPageOrderingParam {
  return (LocalStore.get(ORDER_BY_MODIFIED_DATE_STORAGE_KEY) ||
    'changed_at') as TripsPageOrderingParam;
}

export function setDefaultTripLoadsOrdering(ordering: TripsPageOrderingParam) {
  if (ordering) {
    LocalStore.set(ORDER_BY_MODIFIED_DATE_STORAGE_KEY, ordering);
  } else {
    LocalStore.delete(ORDER_BY_MODIFIED_DATE_STORAGE_KEY);
  }
}

//
// Trip form
//

export type AddressVenueDTO = z.infer<typeof addressVenueSchema>;
const addressVenueShape = {
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
};

const addressVenueSchema = z.object(addressVenueShape);

export type AddressDTO = z.infer<typeof addressSchema>;
const addressSchema = z.object({
  ...addressVenueShape,
  latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export type CreateTripLoadDTO = z.infer<typeof createTripLoadSchema>;
const createTripLoadSchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  load_id: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  pickup_latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  pickup_longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  delivery_latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  delivery_longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  origin: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  destination: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

const tripFormShape = {
  loads: z.array(createTripLoadSchema).prefault([]),
  start_address: addressSchema.nullable().default(null),
  end_address: addressSchema.nullable().default(null),
  start_address_guid: z
    .string()
    .nullish()
    .transform((value) => value ?? '')
    .optional(),
  end_address_guid: z
    .string()
    .nullish()
    .transform((value) => value ?? '')
    .optional(),
};

export const tripFormSchema = z.object(tripFormShape);

export const assignDriverSchema = z.object({
  newDriverGUID: z
    .string()
    .nullish()
    .transform((value) => value ?? '')
    .refine((value) => value.length > 0, 'This field is required')
    .prefault(''),
  newDriverName: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export type TripCreateDTO = z.infer<typeof tripCreateSchema>;
export const tripCreateSchema = z.object({
  ...tripFormShape,
  name: z
    .string()
    .max(100, 'Maximum length is 100 characters')
    .nullish()
    .transform((value) => value ?? '')
    .refine((value) => value.length > 0, 'Enter trip name')
    .prefault(''),
  driver: z
    .object({
      name: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      email: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      guid: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
    })
    .nullable()
    .default(null),
});

//
// PointDTO
//

export type PointDTO = z.infer<typeof pointSchema>;
const pointSchema = z.object({
  trip_guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  type: z.enum(['origin', 'destination']),
});

//
// TripsCountResponse
//

export type TripsCountResponse = z.infer<typeof tripsCountSchema>;
export const tripsCountSchema = z.object({
  active: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  archived: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export const TRIPS_FILTER_FIELDS = ['driver_guid', 'dispatcher_guid'] as const;
export type TripsFilterField = (typeof TRIPS_FILTER_FIELDS)[number];

//
// TripPointDTO
//

export type TripPointVehicleDTO = z.infer<typeof tripPointVehicleSchema>;
const tripPointVehicleSchema = z.object({
  is_inoperable: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  requires_enclosed_trailer: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  maker: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  model: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  type: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  year: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export type TripPointDTO = z.infer<typeof tripPointSchema>;
const tripPointSchema = z.object({
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  is_done: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  is_terminal: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  terminal_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  sequential_number: z.preprocess(
    (value) => (value == null || value === '' ? 0 : Number(value)),
    z.number().default(0).catch(0),
  ),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  type: z
    .enum([
      'origin',
      'destination',
      'pickup',
      'delivery',
      'delivery_hovered',
      'pickup_hovered',
      'delivery_done',
      'pickup_done',
    ])
    .catch('origin'),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  actual_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  scheduled_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  load: z
    .object({
      guid: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      number: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      customer_name: z
        .string()
        .nullish()
        .transform((value) => value ?? ''),
      date: z.preprocess(
        (value) =>
          stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
        z.string().nullable().default(null),
      ),
      vehicles: z.array(tripPointVehicleSchema).prefault([]),
    })
    .nullable(),
});

//
// TripStatisticsDTO
//

export type TripStatisticsDTO = z.infer<typeof tripStatisticsSchema>;
export const tripStatisticsSchema = z.object({
  vehicle_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  vehicle_weight: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  distance: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  deadhead: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  repositioning: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

//
// TripPaymentDTO
//

const tripPaymentItemSchema = z.object({
  // Backend does not support empty strings.
  payment_method: z.preprocess(
    (value) => value || null,
    z.custom<PaymentMethod>().nullable(),
  ),
  // Backend may return both 'quickpay' and 'quick_pay'.
  payment_terms: z.preprocess(
    (value) => (value === 'quickpay' ? 'quick_pay' : value) || null,
    z.custom<PaymentTerm>().nullable(),
  ),

  customer_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  amount: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  status: z.enum(['billed', 'paid']).nullable().default(null).catch(null),
  load_guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  load_number: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
});

export type TripPaymentDTO = z.infer<typeof tripPaymentSchema>;
export const tripPaymentSchema = z.object({
  cash: z.array(tripPaymentItemSchema).prefault([]),
  billing: z.array(tripPaymentItemSchema).prefault([]),
  broker_fee: z
    .array(
      z.object({
        customer_name: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
        amount: z.preprocess(
          (value) => (value == null || value === '' ? null : Number(value)),
          z.number().nullable().default(null).catch(null),
        ),
        load_guid: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
        load_number: z
          .string()
          .nullish()
          .transform((value) => value ?? ''),
      }),
    )
    .prefault([]),
  total_expense: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  total_price: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  total_driver_pay: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

//
// TripLoadDTO
//

export type TripLoadAddressDTO = z.infer<typeof tripLoadAddressSchema>;
const tripLoadAddressSchema = z.object({
  address: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  city: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  state: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  zip: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  latitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  longitude: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

export type TripLoadDTO = z.infer<typeof tripLoadSchema>;
const tripLoadSchema = z.object({
  guid: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  number: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  status: z.enum(LOAD_STATUSES).catch('new'),
  customer_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),

  origin: tripLoadAddressSchema,
  destination: tripLoadAddressSchema,

  is_in_terminal: z.preprocess(
    coerceBoolean,
    z.boolean().nullable().default(false).catch(false),
  ),
  terminal_name: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),

  vehicles: z.array(tripPointVehicleSchema).prefault([]),
  scheduled_pickup_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  scheduled_delivery_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  actual_pickup_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),
  actual_delivery_date: z.preprocess(
    (value) =>
      stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }),
    z.string().nullable().default(null),
  ),

  terminals: z.array(loadTerminalSchema).prefault([]),
  current_leg: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});

//
// TripCountsDTO
//

export type TripCountsDTO = z.infer<typeof tripCountsSchema>;
export const tripCountsSchema = z.object({
  loads_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
  points_count: z.preprocess(
    (value) => (value == null || value === '' ? null : Number(value)),
    z.number().nullable().default(null).catch(null),
  ),
});
